import React, { useState, useEffect, useContext } from 'react';
import { ShieldAlert, Activity, AlertTriangle, AlertCircle, RefreshCw, Send, CheckCircle2 } from 'lucide-react';
import { SnackbarContext } from '../App';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
    SheetFooter
} from "./ui/sheet";
import api from '../api/api';
import AdoptionStats from './AdoptionStats';
import PageShell from './ui/PageShell';
import PageHeader from './ui/PageHeader';

export default function OpsDashboard() {
    const showSnackbar = useContext(SnackbarContext);

    const [activeTab, setActiveTab] = useState('summary');
    const [summary, setSummary] = useState(null);
    const [alerts, setAlerts] = useState([]);
    const [violations, setViolations] = useState([]);
    const [outbox, setOutbox] = useState([]);
    const [unified, setUnified] = useState(null);
    const [loading, setLoading] = useState(true);

    const [selectedOutboxItem, setSelectedOutboxItem] = useState(null);
    const [isOutboxModalOpen, setIsOutboxModalOpen] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [sumRes, alertsRes, violRes, outboxRes, unifiedRes] = await Promise.all([
                api.get('/api/ops/summary'),
                api.get('/api/ops/alerts'),
                api.get('/api/ops/violations'),
                api.get('/api/ops/outbox'),
                api.get('/api/ops/unified')
            ]);

            setSummary(sumRes.data);
            setAlerts(alertsRes.data);
            setViolations(violRes.data);
            setOutbox(outboxRes.data);
            setUnified(unifiedRes.data);
        } catch (error) {
            handleApiError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleApiError = (error) => {
        if (error.isAppError) {
            showSnackbar(`${error.code}: ${error.message}`, 'error');
        } else {
            showSnackbar('An unexpected error occurred reading Ops data.', 'error');
        }
    };

    const handleResolveAlert = async (id) => {
        try {
            await api.put(`/api/security/alerts/${id}/resolve`, {});
            showSnackbar('Alert resolved successfully.', 'success');
            fetchData();
        } catch (error) {
            handleApiError(error);
        }
    };

    const handleReplayRequest = async (id) => {
        try {
            const res = await api.post(`/api/ops/outbox/${id}/replay`, {});
            if (res.status === 202) {
                showSnackbar(`Replay queued for Approval: ${res.data.status}`, 'default');
            } else {
                showSnackbar('Replay started successfully', 'success');
            }
            setIsOutboxModalOpen(false);
            fetchData();
        } catch (error) {
            handleApiError(error);
        }
    };

    const openOutboxDetails = (item) => {
        setSelectedOutboxItem(item);
        setIsOutboxModalOpen(true);
    };

    const getAlertColor = (severity) => {
        switch (severity) {
            case 'CRITICAL': return 'bg-red-100 text-red-800 border-red-200';
            case 'HIGH': return 'bg-orange-100 text-orange-800 border-orange-200';
            case 'MEDIUM': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            default: return 'bg-blue-100 text-blue-800 border-blue-200';
        }
    };

    if (loading && !summary) {
        return <div className="p-8 text-center text-muted-foreground">Loading Security & Ops Data...</div>;
    }

    return (
        <PageShell className="space-y-6">
            <PageHeader
                title="Security & Ops Center"
                subtitle="System health, abuse monitoring, and background worker state."
                icon={ShieldAlert}
                actions={
                    <Button onClick={fetchData} variant="outline" size="sm" className="bg-card hover:bg-muted text-foreground font-bold shadow-sm rounded-xl">
                        <RefreshCw className="mr-2 h-4 w-4" /> Refresh
                    </Button>
                }
            />

            {/* Top Summary Cards */}
            {summary && (
                <div className="grid gap-4 md:grid-cols-3">
                    <div className="rounded-xl border bg-card text-card-foreground shadow p-6 flex flex-col items-center text-center">
                        <ShieldAlert className={`h-8 w-8 mb-2 ${summary.openAlerts > 0 ? 'text-destructive' : 'text-muted-foreground'}`} />
                        <h3 className="text-3xl font-bold">{summary.openAlerts}</h3>
                        <p className="text-sm text-muted-foreground">Open Security Alerts</p>
                    </div>
                    <div className="rounded-xl border bg-card text-card-foreground shadow p-6 flex flex-col items-center text-center">
                        <Activity className={`h-8 w-8 mb-2 ${summary.recentFailures > 50 ? 'text-destructive' : summary.recentFailures > 0 ? 'text-orange-500' : 'text-muted-foreground'}`} />
                        <h3 className="text-3xl font-bold">{summary.recentFailures}</h3>
                        <p className="text-sm text-muted-foreground">Failures (24h)</p>
                    </div>
                    <div className="rounded-xl border bg-card text-card-foreground shadow p-6 flex flex-col items-center text-center">
                        <Send className={`h-8 w-8 mb-2 ${summary.pendingNotifications > 100 ? 'text-orange-500' : 'text-muted-foreground'}`} />
                        <h3 className="text-3xl font-bold">{summary.pendingNotifications}</h3>
                        <p className="text-sm text-muted-foreground">Pending Outbox / Retrying</p>
                    </div>
                </div>
            )}

            {/* Navigation Tabs */}
            <div className="flex space-x-2 border-b">
                <button
                    className={`px-4 py-2 font-medium text-sm ${activeTab === 'summary' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                    onClick={() => setActiveTab('summary')}
                >
                    Active Alerts
                </button>
                <button
                    className={`px-4 py-2 font-medium text-sm ${activeTab === 'violations' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                    onClick={() => setActiveTab('violations')}
                >
                    Audit Violations (Raw)
                </button>
                <button
                    className={`px-4 py-2 font-medium text-sm ${activeTab === 'outbox' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                    onClick={() => setActiveTab('outbox')}
                >
                    Outbox & Dead Letter
                </button>
                <button
                    className={`px-4 py-2 font-medium text-sm ${activeTab === 'adoption' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                    onClick={() => setActiveTab('adoption')}
                >
                    User Adoption & Activation
                </button>
            </div>

            {/* Tab Content */}
            <div className="bg-card border rounded-lg shadow-sm">

                {/* ALERTS TAB */}
                {activeTab === 'summary' && (
                    <div className="p-6">
                        {alerts.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                                <CheckCircle2 className="h-10 w-10 mx-auto opacity-30 mb-2" />
                                No pending security alerts. Clean bill of health.
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {alerts.map(alert => (
                                    <div key={alert._id} className="flex justify-between items-start p-4 border rounded relative overflow-hidden">
                                        {alert.status === 'RESOLVED' && <div className="absolute inset-0 bg-white/60 dark:bg-black/60 z-10" />}
                                        <div className="z-20">
                                            <div className="flex items-center space-x-3 mb-1">
                                                <Badge className={getAlertColor(alert.severity)} variant="outline">
                                                    {alert.severity}
                                                </Badge>
                                                <span className="font-semibold text-lg">{alert.eventCategory}</span>
                                            </div>
                                            <p className="text-sm text-muted-foreground max-w-2xl">{alert.description}</p>
                                            <div className="text-xs text-muted-foreground mt-2 font-mono bg-muted p-1 rounded w-max">
                                                Context: {JSON.stringify(alert.contextData)}
                                            </div>
                                            <div className="text-xs text-muted-foreground mt-1">
                                                {new Date(alert.createdAt).toLocaleString()}
                                            </div>
                                        </div>
                                        <div className="z-20">
                                            {alert.status === 'OPEN' ? (
                                                <Button size="sm" onClick={() => handleResolveAlert(alert._id)}>
                                                    Acknowledge & Resolve
                                                </Button>
                                            ) : (
                                                <Badge variant="outline">Resolved</Badge>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* VIOLATIONS TAB */}
                {activeTab === 'violations' && (
                    <div className="p-6">
                        {violations.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">No failure violations found.</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left">
                                    <thead className="bg-muted text-muted-foreground text-xs uppercase">
                                        <tr>
                                            <th className="px-4 py-2">Time</th>
                                            <th className="px-4 py-2">Action</th>
                                            <th className="px-4 py-2">Resource</th>
                                            <th className="px-4 py-2">User / Identity</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                        {violations.map(v => (
                                            <tr key={v._id} className="hover:bg-muted/50">
                                                <td className="px-4 py-2 whitespace-nowrap">{new Date(v.createdAt).toLocaleString()}</td>
                                                <td className="px-4 py-2 font-medium text-destructive">{v.action}</td>
                                                <td className="px-4 py-2">{v.resource || '-'}</td>
                                                <td className="px-4 py-2 text-xs">
                                                    {v.userId ? v.userId : 'System/Anonymous'} <br />
                                                    <span className="text-muted-foreground">IP: {v.ipAddress}</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* OUTBOX TAB */}
                {activeTab === 'outbox' && (
                    <div className="p-6">
                        {outbox.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">Outbox is empty.</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left">
                                    <thead className="bg-muted text-muted-foreground text-xs uppercase">
                                        <tr>
                                            <th className="px-4 py-2">Event</th>
                                            <th className="px-4 py-2">Status</th>
                                            <th className="px-4 py-2">Retries</th>
                                            <th className="px-4 py-2">Last Error (If Any)</th>
                                            <th className="px-4 py-2">Created</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                        {outbox.map(o => (
                                            <tr key={o._id} className="hover:bg-muted/50 cursor-pointer" onClick={() => openOutboxDetails(o)}>
                                                <td className="px-4 py-2 font-medium">{o.eventType}</td>
                                                <td className="px-4 py-2">
                                                    <Badge variant={
                                                        o.status === 'SENT' ? 'success' :
                                                            o.status === 'DEAD_LETTER' ? 'destructive' :
                                                                o.status === 'RETRYING' ? 'warning' : 'outline'
                                                    }>
                                                        {o.status}
                                                    </Badge>
                                                </td>
                                                <td className="px-4 py-2">{o.retryCount}</td>
                                                <td className="px-4 py-2 truncate max-w-[200px] text-xs" title={o.lastError}>
                                                    {o.lastError || '-'}
                                                </td>
                                                <td className="px-4 py-2 whitespace-nowrap text-xs">
                                                    {new Date(o.createdAt).toLocaleString()}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* UNIFIED AUTOMATION TAB */}
                {activeTab === 'unified' && (
                    <div className="p-6">
                        {unified ? (
                            <div className="space-y-6">
                                <div className="grid gap-4 md:grid-cols-4">
                                    <div className="rounded-xl border bg-card text-card-foreground shadow p-6 flex flex-col items-center text-center">
                                        <Activity className="h-6 w-6 mb-2 text-blue-500" />
                                        <h3 className="text-2xl font-bold">{unified.workflows?.active || 0}</h3>
                                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Active Workflows</p>
                                    </div>
                                    <div className="rounded-xl border bg-card text-card-foreground shadow p-6 flex flex-col items-center text-center">
                                        <AlertTriangle className="h-6 w-6 mb-2 text-yellow-500" />
                                        <h3 className="text-2xl font-bold">{unified.workflows?.paused || 0}</h3>
                                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Paused Workflows</p>
                                    </div>
                                    <div className="rounded-xl border bg-card text-card-foreground shadow p-6 flex flex-col items-center text-center">
                                        <AlertCircle className={`h-6 w-6 mb-2 ${unified.workflowRuns?.failed > 0 ? 'text-destructive' : 'text-muted-foreground'}`} />
                                        <h3 className="text-2xl font-bold">{unified.workflowRuns?.failed || 0}</h3>
                                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Failed Runs</p>
                                    </div>
                                    <div className="rounded-xl border bg-card text-card-foreground shadow p-6 flex flex-col items-center text-center">
                                        <ShieldAlert className={`h-6 w-6 mb-2 ${unified.approvals?.pending > 0 ? 'text-orange-500' : 'text-muted-foreground'}`} />
                                        <h3 className="text-2xl font-bold">{unified.approvals?.pending || 0}</h3>
                                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Pending Approvals</p>
                                    </div>
                                </div>
                                {unified.recentFailedRuns && unified.recentFailedRuns.length > 0 && (
                                    <div>
                                        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Recent Failed Workflow Runs</h3>
                                        <div className="space-y-2">
                                            {unified.recentFailedRuns.map(run => (
                                                <div key={run._id} className="flex justify-between items-center border rounded p-3">
                                                    <div>
                                                        <span className="font-medium text-sm">{run.workflowId?.name || 'Unknown'}</span>
                                                        <span className="text-xs text-muted-foreground ml-3">{run.targetEntityType} [{run.targetEntityId}]</span>
                                                    </div>
                                                    <div className="flex items-center space-x-3">
                                                        {run.lastError && <span className="text-xs text-destructive truncate max-w-[200px]" title={run.lastError}>{run.lastError}</span>}
                                                        <Badge variant="destructive">FAILED</Badge>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-12 text-muted-foreground">Loading unified automation data...</div>
                        )}
                    </div>
                )}

                {/* ADOPTION TAB */}
                {activeTab === 'adoption' && (
                    <AdoptionStats />
                )}

            </div>

            {/* Outbox Detail Modal */}
            <Sheet open={isOutboxModalOpen} onOpenChange={setIsOutboxModalOpen}>
                <SheetContent side="right" className="max-w-2xl">
                    <SheetHeader>
                        <SheetTitle>Outbox Execution Detail</SheetTitle>
                        <SheetDescription>
                            Review internal notification channel processing attempts.
                        </SheetDescription>
                    </SheetHeader>

                    {selectedOutboxItem && (
                        <div className="space-y-4 py-2">
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div><span className="font-semibold">Event:</span> {selectedOutboxItem.eventType}</div>
                                <div><span className="font-semibold">Channel:</span> {selectedOutboxItem.channel}</div>
                                <div><span className="font-semibold">Status:</span> <Badge>{selectedOutboxItem.status}</Badge></div>
                                <div><span className="font-semibold">Attempts:</span> {selectedOutboxItem.attemptCount}</div>
                                <div><span className="font-semibold">Created:</span> {new Date(selectedOutboxItem.createdAt).toLocaleString()}</div>
                                <div><span className="font-semibold">Next Attempt:</span> {selectedOutboxItem.nextAttemptAt ? new Date(selectedOutboxItem.nextAttemptAt).toLocaleString() : 'N/A'}</div>
                            </div>

                            <div>
                                <h4 className="font-medium text-sm mb-1 mt-4">Targeted Payload:</h4>
                                <pre className="bg-muted p-3 rounded-md text-xs overflow-x-auto">
                                    {JSON.stringify(selectedOutboxItem.payload, null, 2)}
                                </pre>
                            </div>

                            {selectedOutboxItem.lastError && (
                                <div>
                                    <h4 className="font-medium text-sm text-destructive mb-1 mt-2">Latest Failure Record:</h4>
                                    <pre className="bg-destructive/10 text-destructive p-3 rounded-md text-xs whitespace-pre-wrap">
                                        {selectedOutboxItem.lastError}
                                    </pre>
                                </div>
                            )}
                        </div>
                    )}

                    <SheetFooter className="mt-4">
                        <Button variant="outline" onClick={() => setIsOutboxModalOpen(false)}>Close</Button>
                        {selectedOutboxItem && (selectedOutboxItem.status === 'DEAD_LETTER' || selectedOutboxItem.status === 'FAILED') && (
                            <Button
                                variant="default"
                                onClick={() => handleReplayRequest(selectedOutboxItem._id)}>
                                <RefreshCw className="mr-2 h-4 w-4" /> Request Replay (Maker/Checker)
                            </Button>
                        )}
                    </SheetFooter>
                </SheetContent>
            </Sheet>

        </PageShell>
    );
}

